import React from 'react';
import { Eye, MoreVertical, PlayCircle, FileSearch, Trash2, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { ContractStatus, StatusBadge, INTERRUPTED_STATES } from '../../lib/contract-status';

export type { ContractStatus } from '../../lib/contract-status';

export interface Contract {
  id: string;
  name: string;
  state: string;
  status: ContractStatus;
  effective_date: string;
  expiration_date?: string;
  version?: string;
  created_at: string;
  updated_at: string;
  analysis_status?: string;
  gap_count?: number;
}

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
}

export interface ContractsTableProps {
  contracts: Contract[];
  isLoading?: boolean;
  onView?: (contract: Contract) => void;
  onProcess?: (contract: Contract) => void;
  onAnalyze?: (contract: Contract) => void;
  onDelete?: (contract: Contract) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  pagination?: PaginationProps;
}


const stateLabels: Record<string, string> = {
  PR: 'Puerto Rico',
  FL: 'Florida',
  TX: 'Texas',
  CA: 'California',
  NY: 'New York',
  LA: 'Louisiana',
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};


const ActionMenu: React.FC<{
  contract: Contract;
  onView?: (contract: Contract) => void;
  onProcess?: (contract: Contract) => void;
  onAnalyze?: (contract: Contract) => void;
  onDelete?: (contract: Contract) => void;
}> = ({ contract, onView, onProcess, onAnalyze, onDelete }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const canProcess = contract.status === 'uploaded' || contract.status === 'error';
  const canResume = INTERRUPTED_STATES.includes(contract.status);
  const canAnalyze = ['structured', 'embedded', 'linked', 'ready'].includes(contract.status);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-2 hover:bg-neutral-100 rounded-lg text-neutral-500 hover:text-neutral-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        aria-label="Actions"
        data-testid={`contract-actions-${contract.id}`}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-dropdown border border-neutral-200 z-20 overflow-hidden"
          data-testid={`contract-actions-menu-${contract.id}`}
        >
          <div className="py-1">
            {onView && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onView(contract);
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors"
                data-testid={`action-view-${contract.id}`}
              >
                <Eye className="h-4 w-4 mr-3 text-neutral-500" />
                View Details
              </button>
            )}
            {onProcess && canProcess && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onProcess(contract);
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors"
                data-testid={`action-process-${contract.id}`}
              >
                <PlayCircle className="h-4 w-4 mr-3 text-primary-600" />
                Process Document
              </button>
            )}
            {onProcess && canResume && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onProcess(contract);
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors"
                data-testid={`action-resume-${contract.id}`}
              >
                <RefreshCw className="h-4 w-4 mr-3 text-warning-600" />
                Resume Processing
              </button>
            )}
            {onAnalyze && canAnalyze && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAnalyze(contract);
                  setIsOpen(false);
                }}
                className="flex items-center w-full px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors"
                data-testid={`action-analyze-${contract.id}`}
              >
                <FileSearch className="h-4 w-4 mr-3 text-primary-600" />
                Run Analysis
              </button>
            )}
            {onDelete && (
              <>
                <div className="border-t border-neutral-100 my-1" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(contract);
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-colors"
                  data-testid={`action-delete-${contract.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-3" />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Pagination component
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-white border-t border-neutral-200"
      data-testid="pagination"
    >
      {/* Items per page selector */}
      <div className="flex items-center gap-2 text-sm text-neutral-600">
        <span>Show</span>
        {onItemsPerPageChange ? (
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="border border-neutral-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            data-testid="items-per-page-select"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        ) : (
          <span className="font-medium">{itemsPerPage}</span>
        )}
        <span>of {totalItems} items</span>
      </div>

      {/* Page info and navigation */}
      <div className="flex items-center gap-1">
        <span className="text-sm text-neutral-600 mr-4 hidden sm:inline">
          {startItem}-{endItem} of {totalItems}
        </span>

        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-md border border-neutral-300 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
          aria-label="Previous page"
          data-testid="pagination-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-2 py-1 text-neutral-400">...</span>
              ) : (
                <button
                  onClick={() => onPageChange(page as number)}
                  className={`min-w-[36px] h-9 px-3 py-1 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
                    currentPage === page
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'border border-neutral-300 text-neutral-700 hover:bg-neutral-50 hover:border-neutral-400'
                  }`}
                  data-testid={`pagination-page-${page}`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-md border border-neutral-300 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-400 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
          aria-label="Next page"
          data-testid="pagination-next"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Sortable header component with enhanced styling
const SortableHeader: React.FC<{
  label: string;
  field: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}> = ({ label, field, sortBy, sortOrder, onSort }) => {
  const isActive = sortBy === field;

  return (
    <th
      className="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider cursor-pointer hover:bg-neutral-100 transition-colors select-none group"
      onClick={() => onSort?.(field)}
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-2">
        {label}
        <span className={`transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
          {isActive && sortOrder === 'asc' ? (
            <ArrowUp className="h-4 w-4 text-primary-600" />
          ) : isActive && sortOrder === 'desc' ? (
            <ArrowDown className="h-4 w-4 text-primary-600" />
          ) : (
            <ArrowUp className="h-4 w-4 text-neutral-400" />
          )}
        </span>
      </div>
    </th>
  );
};

// Table loading skeleton
const TableSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-card border border-neutral-100 overflow-hidden" data-testid="contracts-table-loading">
    <div className="animate-pulse">
      {/* Header skeleton */}
      <div className="h-12 bg-neutral-100 flex items-center px-6 gap-8">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-3 bg-neutral-200 rounded w-20" />
        ))}
      </div>
      {/* Row skeletons */}
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className={`h-16 border-t border-neutral-100 flex items-center px-6 gap-8 ${i % 2 === 0 ? 'bg-neutral-50/50' : 'bg-white'}`}>
          <div className="h-4 bg-neutral-200 rounded w-32" />
          <div className="h-5 bg-neutral-200 rounded-full w-16" />
          <div className="h-5 bg-neutral-200 rounded-full w-20" />
          <div className="h-3 bg-neutral-200 rounded w-24" />
          <div className="h-3 bg-neutral-200 rounded w-24" />
          <div className="h-8 bg-neutral-200 rounded w-8 ml-auto" />
        </div>
      ))}
    </div>
  </div>
);

// Empty state component
const EmptyState: React.FC = () => (
  <div className="p-12 text-center" data-testid="contracts-table-empty">
    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-neutral-100 flex items-center justify-center">
      <FileSearch className="h-8 w-8 text-neutral-400" />
    </div>
    <h3 className="text-lg font-semibold text-neutral-900 mb-1">No contracts found</h3>
    <p className="text-sm text-neutral-500 mb-4">
      Upload a contract to get started with analysis.
    </p>
  </div>
);

export const ContractsTable: React.FC<ContractsTableProps> = ({
  contracts,
  isLoading,
  onView,
  onProcess,
  onAnalyze,
  onDelete,
  sortBy,
  sortOrder,
  onSort,
  pagination,
}) => {
  if (isLoading) {
    return <TableSkeleton />;
  }

  if (contracts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-card border border-neutral-100 overflow-hidden">
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-card border border-neutral-100 hover:shadow-card-hover transition-shadow" data-testid="contracts-table">
      {/* Responsive table wrapper with horizontal scroll indicator */}
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100 rounded-xl">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              <SortableHeader
                label="Contract Name"
                field="name"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                State/Territory
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                Status
              </th>
              <SortableHeader
                label="Created"
                field="created_at"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-100">
            {contracts.map((contract, index) => (
              <tr
                key={contract.id}
                className={`hover:bg-primary-50/50 transition-colors cursor-pointer ${
                  index % 2 === 1 ? 'bg-neutral-50/50' : 'bg-white'
                }`}
                data-testid={`contract-row-${contract.id}`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div
                        className="text-sm font-medium text-neutral-900 hover:text-primary-600 cursor-pointer transition-colors"
                        onClick={() => onView?.(contract)}
                        data-testid={`contract-name-${contract.id}`}
                      >
                        {contract.name}
                      </div>
                      <div className="text-xs text-neutral-400 font-mono mt-0.5">
                        ID: {contract.id}
                      </div>
                      {contract.version && (
                        <div className="text-xs text-neutral-500 mt-0.5">
                          Version {contract.version}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700"
                    data-testid={`contract-state-${contract.id}`}
                  >
                    {stateLabels[contract.state] || contract.state}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={contract.status} />
                    {contract.gap_count !== undefined && contract.gap_count > 0 && (
                      <span
                        className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full"
                        data-testid={`contract-gaps-${contract.id}`}
                      >
                        {contract.gap_count} gaps
                      </span>
                    )}
                  </div>
                </td>
                <td
                  className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600"
                  data-testid={`contract-created-${contract.id}`}
                >
                  {formatDate(contract.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <ActionMenu
                    contract={contract}
                    onView={onView}
                    onProcess={onProcess}
                    onAnalyze={onAnalyze}
                    onDelete={onDelete}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <Pagination {...pagination} />
      )}
    </div>
  );
};
